import type { TFunction } from "i18next";

import type { DigitalAccessUser } from "@/api/digital-access";
import {
  DA_DOC_TYPE_NAMES,
  DA_DOC_TYPES,
  documentsByType,
  type DaItem,
  type DigitalAccessFrame,
} from "@/components/projects/diagnostic/selectors/digitalAccess";

type Cell = string | number | null;

/**
 * Exportação em Excel do Digital Access (#1346 fatia 8, EF-FUNC-005 RF-05).
 *
 * Duas abas: os itens do quadro do Ato 1 e os usuários técnicos. Os números vão
 * crus, para a planilha somar; piso e item não aplicável vão escritos numa
 * coluna, porque a planilha não tem o "+" nem o cinza da tela para dizer isso.
 */
function itemSituation(item: DaItem, t: TFunction): string {
  if (!item.applicable) return t("Not applicable");
  if (!item.collected) return t("Not collected");
  return floorSituation(item, t);
}

/** Qual número é piso, como o `+` da tela diz por coluna (#1521). */
function floorSituation(floor: { estimateFloor: boolean; totalFloor: boolean }, t: TFunction): string {
  if (floor.estimateFloor) return t("Floor: the count hit the ceiling");
  if (floor.totalFloor) return t("Floor only in the total posted: the Digital Access documents are complete");
  return "";
}

export function digitalAccessItemsMatrix(frame: DigitalAccessFrame, t: TFunction): Cell[][] {
  const header: Cell[] = [
    t("Document type"),
    t("Item (SAP ID)"),
    t("Description"),
    t("Table"),
    t("Total posted"),
    t("Created by integration users ({{types}})", { types: frame.countedUserTypes.join(", ") }),
    t("Factor"),
    t("Digital Access documents"),
    t("Situation"),
  ];
  const rows: Cell[][] = frame.groups.flatMap((group) =>
    group.items.map((item) => [
      DA_DOC_TYPE_NAMES[item.docType],
      item.itemId,
      item.name,
      item.tables.join(", "),
      item.total ?? null,
      item.counted ?? null,
      item.factor ?? null,
      item.daDocuments ?? null,
      itemSituation(item, t),
    ]),
  );
  const total: Cell[] = [
    t("Estimated Digital Access total"),
    "",
    "",
    "",
    frame.totals.total,
    frame.totals.counted,
    "",
    frame.totals.daDocuments,
    floorSituation(frame.totals, t),
  ];
  return [header, ...rows, total];
}

function counts(user: DigitalAccessUser, t: TFunction): string {
  if (user.decision === "include") return t("Yes");
  if (user.decision === "exclude") return t("No");
  if (user.decision === "client_validation") return t("To confirm with the client");
  return user.counts_by_rule ? t("Yes (rule)") : t("No (rule)");
}

export function digitalAccessUsersMatrix(users: DigitalAccessUser[], t: TFunction): Cell[][] {
  const header: Cell[] = [
    t("User"),
    t("Type (USTYP)"),
    ...DA_DOC_TYPES.map((docType) => DA_DOC_TYPE_NAMES[docType]),
    t("Total"),
    t("Count in the Digital Access?"),
    t("Note"),
  ];
  const rows = users.map((user) => {
    const byType = documentsByType(user.by_item ?? {});
    return [
      user.username,
      user.user_type,
      ...DA_DOC_TYPES.map((docType) => byType[docType] ?? 0),
      user.total,
      counts(user, t),
      user.note ?? "",
    ];
  });
  return [header, ...rows];
}

/**
 * Baixa a planilha; o SheetJS só carrega no clique. Sem o quadro (projeto sem a
 * seção de Digital Access na tela), sai só a aba dos usuários.
 */
export async function downloadDigitalAccessWorkbook(
  frame: DigitalAccessFrame | null,
  users: DigitalAccessUser[],
  t: TFunction,
): Promise<void> {
  const XLSX = await import("xlsx");
  const workbook = XLSX.utils.book_new();
  if (frame) {
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(digitalAccessItemsMatrix(frame, t)), t("Items"));
  }
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(digitalAccessUsersMatrix(users, t)), t("Users"));
  const today = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(workbook, `digital-access-${today}.xlsx`);
}
